import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject, getBlob } from 'firebase/storage';
import { getPlayerProfile, canEditPlayerProfile } from './firestoreService';

/**
 * Upload a profile image to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} userId - The user ID for the file path
 * @param {string} profileId - The profile ID for the file path
 * @returns {Promise<string>} - Returns the download URL of the uploaded image
 */
export const uploadProfileImage = async (file, userId, profileId) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload an image smaller than 5MB.');
    }

    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `profile_${timestamp}.${fileExtension}`;
    
    // Create storage reference
    const storageRef = ref(storage, `profile-images/${userId}/${profileId}/${fileName}`);
    
    // Upload file
  
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

export const uploadEventLogo = async (file, userId, eventId) => {
  try {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload an image smaller than 5MB.');
    }

    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `event-logo_${timestamp}.${fileExtension}`;

    const storageRef = ref(storage, `event-logos/${userId}/${eventId}/${fileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading event logo:', error);
    throw error;
  }
};

/**
 * Delete a profile image from Firebase Storage
 * @param {string} imageUrl - The full URL of the image to delete
 * @param {string} userId - The user ID attempting the deletion (optional for backward compatibility)
 * @param {string} profileId - The profile ID being modified (optional for backward compatibility)
 * @param {boolean} isAdmin - Whether the user is an admin (optional for backward compatibility)
 * @returns {Promise<void>}
 */
export const deleteProfileImage = async (imageUrl, userId = null, profileId = null, isAdmin = null) => {
  try {
    if (!imageUrl || !imageUrl.includes('firebase')) {
      return;
    }

    // If ownership validation parameters are provided, validate permission
    if (userId && profileId) {
      try {
        const profileData = await getPlayerProfile(profileId);
        if (!profileData) {
          throw new Error('Profile not found');
        }
        
        if (!canEditPlayerProfile(profileData, userId, isAdmin)) {
          throw new Error('Access denied: You do not have permission to delete images from this profile');
        }
      } catch (error) {
        console.error('Profile ownership validation failed:', error);
        throw error;
      }
    }

    // Extract the file path from the URL
    const urlParts = imageUrl.split('/');
    const fileToken = urlParts[urlParts.length - 1].split('?')[0];
    const pathParts = decodeURIComponent(fileToken).split('/');
    
    if (pathParts[0] === 'profile-images') {
      const storageRef = ref(storage, decodeURIComponent(fileToken));
      await deleteObject(storageRef);
    }
  } catch (error) {
    console.error('Error deleting previous image:', error);
    // For profile image deletion, we might want to throw the error to prevent unauthorized deletions
    if (userId && profileId && (error.message.includes('Access denied') || error.message.includes('Profile not found'))) {
      throw error;
    }
    // Don't throw error for other cases - deletion failure shouldn't block new upload
  }
};

export const deleteEventLogo = async (imageUrl) => {
  try {
    if (!imageUrl || !imageUrl.includes('firebase')) {
      return;
    }

    const urlParts = imageUrl.split('/');
    const fileToken = urlParts[urlParts.length - 1].split('?')[0];
    const pathParts = decodeURIComponent(fileToken).split('/');

    if (pathParts[0] === 'event-logos') {
      const storageRef = ref(storage, decodeURIComponent(fileToken));
      await deleteObject(storageRef);
    }
  } catch (error) {
    console.error('Error deleting event logo:', error);
  }
};

/**
 * Compress an image file before upload
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width (default: 800)
 * @param {number} quality - JPEG quality (default: 0.8)
 * @returns {Promise<File>} - Returns compressed image file
 */
export const compressImage = async (file, maxWidth = 800, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        const compressedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now()
        });
        resolve(compressedFile);
      }, file.type, quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Create a preview URL for an image file
 * @param {File} file - The image file
 * @returns {string} - Object URL for preview
 */
export const createImagePreview = (file) => {
  return URL.createObjectURL(file);
};

/**
 * Revoke a preview URL to free memory
 * @param {string} url - The object URL to revoke
 */
export const revokeImagePreview = (url) => {
  URL.revokeObjectURL(url);
};

/**
 * Upload multiple images to gallery storage
 * @param {File[]} files - Array of image files to upload
 * @param {string} userId - The user ID for the file path
 * @param {string} profileId - The profile ID for the file path
 * @param {Function} onProgress - Callback for upload progress (fileIndex, progress)
 * @returns {Promise<Array>} - Returns array of upload results with URLs
 */
export const uploadGalleryImages = async (files, userId, profileId, onProgress = null) => {
  try {
    const uploadPromises = files.map(async (file, index) => {
      try {
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
          throw new Error(`Invalid file type for ${file.name}. Please upload a JPEG, PNG, or WebP image.`);
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          throw new Error(`File ${file.name} is too large. Please upload an image smaller than 5MB.`);
        }

        // Compress image
        const compressedFile = await compressImage(file, 1200, 0.8);
        
        // Create unique filename
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2);
        const fileExtension = file.name.split('.').pop();
        const fileName = `gallery_${timestamp}_${randomId}.${fileExtension}`;
        
        // Create storage reference
        const storageRef = ref(storage, `gallery-images/${userId}/${profileId}/${fileName}`);
        
        // Upload with progress tracking
        if (onProgress) {
          onProgress(index, 0);
        }
        
        const snapshot = await uploadBytes(storageRef, compressedFile);
        
        if (onProgress) {
          onProgress(index, 100);
        }
        
        // Get download URL
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        return {
          success: true,
          url: downloadURL,
          fileName: file.name,
          id: `${timestamp}_${randomId}`
        };
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        return {
          success: false,
          error: error.message,
          fileName: file.name
        };
      }
    });

    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error in batch upload:', error);
    throw error;
  }
};

/**
 * Upload a single gallery image
 * @param {File} file - The image file to upload
 * @param {string} userId - The user ID for the file path
 * @param {string} profileId - The profile ID for the file path
 * @returns {Promise<Object>} - Returns upload result with URL and metadata
 */
export const uploadGalleryImage = async (file, userId, profileId) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload an image smaller than 5MB.');
    }

    // Compress image
    const compressedFile = await compressImage(file, 1200, 0.8);

    // Create a unique filename with timestamp and random ID
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);
    const fileExtension = file.name.split('.').pop();
    const fileName = `gallery_${timestamp}_${randomId}.${fileExtension}`;
    
    // Create storage reference
    const storageRef = ref(storage, `gallery-images/${userId}/${profileId}/${fileName}`);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, compressedFile);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      url: downloadURL,
      id: `${timestamp}_${randomId}`,
      fileName: file.name,
      uploadTimestamp: timestamp
    };
  } catch (error) {
    console.error('Error uploading gallery image:', error);
    throw error;
  }
};

/**
 * Delete a gallery image from Firebase Storage
 * @param {string} imageUrl - The full URL of the image to delete
 * @param {string} userId - The user ID attempting the deletion
 * @param {string} profileId - The profile ID being modified
 * @param {boolean} isAdmin - Whether the user is an admin
 * @returns {Promise<void>}
 */
export const deleteGalleryImage = async (imageUrl, userId, profileId, isAdmin = false) => {
  try {
    if (!imageUrl || !imageUrl.includes('firebase')) {
      return;
    }

    // Validate ownership before deletion
    if (!userId || !profileId) {
      throw new Error('User ID and Profile ID are required for gallery image deletion');
    }

    try {
      const profileData = await getPlayerProfile(profileId);
      if (!profileData) {
        throw new Error('Profile not found');
      }
      
      if (!canEditPlayerProfile(profileData, userId, isAdmin)) {
        throw new Error('Access denied: You do not have permission to delete images from this profile');
      }
    } catch (error) {
      console.error('Profile ownership validation failed:', error);
      throw error;
    }

    // Extract the file path from the URL
    const urlParts = imageUrl.split('/');
    const fileToken = urlParts[urlParts.length - 1].split('?')[0];
    const pathParts = decodeURIComponent(fileToken).split('/');
    
    if (pathParts[0] === 'gallery-images') {
      const storageRef = ref(storage, decodeURIComponent(fileToken));
      await deleteObject(storageRef);
    }
  } catch (error) {
    console.error('Error deleting gallery image:', error);
    throw error;
  }
};

/**
 * Get blob from Firebase Storage URL using SDK (bypasses CORS)
 * @param {string} url - Full Firebase Storage download URL
 * @returns {Promise<Blob>} - Image blob
 */
export const getBlobFromUrl = async (url) => {
  try {
    // Extract storage path from URL
    // Format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
    const urlObj = new URL(url);
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);
    if (!pathMatch) {
      throw new Error('Invalid Firebase Storage URL');
    }

    const storagePath = decodeURIComponent(pathMatch[1]);
    const storageRef = ref(storage, storagePath);

    // Use Firebase SDK to get blob (bypasses CORS)
    const blob = await getBlob(storageRef);
    return blob;
  } catch (error) {
    console.error('Error fetching blob from Storage:', error);
    throw error;
  }
};

/**
 * Upload a custom social sharing image (OG image)
 * @param {Blob} blob - The image blob from canvas
 * @param {string} userId - The user ID for the file path
 * @param {string} profileId - The profile ID for the file path
 * @returns {Promise<string>} - Returns the download URL of the uploaded image
 */
export const uploadSocialShareImage = async (blob, userId, profileId) => {
  try {
    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const fileName = `og-image_${timestamp}.png`;

    // Create storage reference
    const storageRef = ref(storage, `social-share-images/${userId}/${profileId}/${fileName}`);

    // Upload blob
    const snapshot = await uploadBytes(storageRef, blob);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading social share image:', error);
    throw error;
  }
};

/**
 * Upload a downloadable file to Firebase Storage (any file type)
 * @param {File} file - The file to upload
 * @param {string} profileId - The profile ID for the file path
 * @param {string} blockId - The block ID for organization
 * @returns {Promise<Object>} - Returns { url, fileName, fileSize, fileType }
 */
export const uploadDownloadFile = async (file, profileId, blockId) => {
  try {
    // Validate file size (max 25MB)
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload a file smaller than 25MB.');
    }

    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const fileName = `${timestamp}_${randomId}.${fileExtension}`;

    // Create storage reference
    const storageRef = ref(storage, `downloads/${profileId}/${blockId}/${fileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      fileName: file.name,
      fileSize: file.size,
      fileType: fileExtension,
    };
  } catch (error) {
    console.error('Error uploading download file:', error);
    throw error;
  }
};

/**
 * Delete a download file from Firebase Storage
 * @param {string} fileUrl - The full URL of the file to delete
 * @returns {Promise<void>}
 */
export const deleteDownloadFile = async (fileUrl) => {
  try {
    if (!fileUrl || !fileUrl.includes('firebase')) {
      return;
    }

    const urlParts = fileUrl.split('/');
    const fileToken = urlParts[urlParts.length - 1].split('?')[0];
    const pathParts = decodeURIComponent(fileToken).split('/');

    if (pathParts[0] === 'downloads') {
      const storageRef = ref(storage, decodeURIComponent(fileToken));
      await deleteObject(storageRef);
    }
  } catch (error) {
    console.error('Error deleting download file:', error);
    // Fail silently — deletion failure shouldn't block the user
  }
};

/**
 * Generate and upload an event-specific social sharing image (OG image)
 * Creates a 1200×630px canvas with event logo, title, and date
 * @param {Object} eventData - Event data containing title, dates, logo, etc.
 * @param {string} userId - The user ID for the file path
 * @param {string} eventId - The event ID for the file path
 * @returns {Promise<string>} - Returns the download URL of the uploaded image
 */
export const generateEventSocialShareImage = async (eventData, userId, eventId) => {
  try {
    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 630;
    const ctx = canvas.getContext('2d');

    // Fill background with gradient using event's primary color or default
    const primaryColor = eventData.primaryColorHex || '#749555';
    const gradient = ctx.createLinearGradient(0, 0, 1200, 630);

    // Create darker shade for gradient end
    const darkerColor = adjustColorBrightness(primaryColor, -30);
    gradient.addColorStop(0, primaryColor);
    gradient.addColorStop(1, darkerColor);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1200, 630);

    let currentY = 80; // Start position for content

    // Draw event logo if available
    if (eventData.eventLogo) {
      try {
        const logo = new Image();
        logo.crossOrigin = 'anonymous';

        // Use getBlobFromUrl for Firebase URLs, otherwise try direct load
        let logoSrc;
        if (eventData.eventLogo.includes('firebasestorage')) {
          const blob = await getBlobFromUrl(eventData.eventLogo);
          logoSrc = URL.createObjectURL(blob);
        } else {
          logoSrc = eventData.eventLogo;
        }

        await new Promise((resolve, reject) => {
          logo.onload = () => resolve();
          logo.onerror = () => reject(new Error('Failed to load event logo'));
          logo.src = logoSrc;
        });

        // Draw logo (max 300×300px, maintain aspect ratio)
        const maxLogoSize = 300;
        let logoWidth = logo.width;
        let logoHeight = logo.height;

        if (logoWidth > maxLogoSize || logoHeight > maxLogoSize) {
          const scale = Math.min(maxLogoSize / logoWidth, maxLogoSize / logoHeight);
          logoWidth = logoWidth * scale;
          logoHeight = logoHeight * scale;
        }

        const logoX = (1200 - logoWidth) / 2;
        ctx.drawImage(logo, logoX, currentY, logoWidth, logoHeight);
        currentY += logoHeight + 30;

        // Clean up object URL if created
        if (logoSrc.startsWith('blob:')) {
          URL.revokeObjectURL(logoSrc);
        }
      } catch (error) {
        console.warn('Failed to load event logo, continuing without it:', error);
        // Continue without logo
      }
    }

    // Draw event title
    ctx.fillStyle = 'white';
    ctx.font = 'bold 48px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const title = eventData.eventTitle || 'Chess Tournament';
    const maxTitleWidth = 1100;

    // Word wrap title if too long
    const words = title.split(' ');
    let line = '';
    const lines = [];

    for (const word of words) {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxTitleWidth && line !== '') {
        lines.push(line);
        line = word + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line);

    // Draw title lines
    lines.forEach((line, index) => {
      ctx.fillText(line.trim(), 600, currentY + (index * 60));
    });
    currentY += (lines.length * 60) + 20;

    // Draw event date
    if (eventData.startDate) {
      ctx.font = '32px sans-serif';
      const dateStr = formatEventDate(eventData.startDate, eventData.endDate, eventData.isSingleDay);
      ctx.fillText(dateStr, 600, currentY);
      currentY += 50;
    }

    // Draw ChessWeb.site branding at bottom
    ctx.font = '24px sans-serif';
    ctx.fillText('ChessWeb.site', 600, 580);

    // Convert canvas to blob
    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/png', 0.9);
    });

    // Upload to Firebase Storage
    const timestamp = Date.now();
    const fileName = `og-image_${timestamp}.png`;
    const storageRef = ref(storage, `event-social-share-images/${userId}/${eventId}/${fileName}`);

    const snapshot = await uploadBytes(storageRef, blob);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error generating event social share image:', error);
    throw error;
  }
};

/**
 * Helper function to adjust color brightness
 * @param {string} color - Hex color string
 * @param {number} amount - Amount to adjust (-100 to 100)
 * @returns {string} - Adjusted hex color
 */
function adjustColorBrightness(color, amount) {
  const num = parseInt(color.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/**
 * Helper function to format event date
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @param {boolean} isSingleDay - Whether it's a single day event
 * @returns {string} - Formatted date string
 */
function formatEventDate(startDate, endDate, isSingleDay) {
  if (!startDate) return '';
  const start = new Date(startDate);
  if (isNaN(start.getTime())) return '';

  if (isSingleDay || !endDate) {
    return start.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  const end = new Date(endDate);
  if (isNaN(end.getTime())) {
    return start.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  if (startDate === endDate) {
    return start.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
}

/**
 * Delete multiple gallery images from Firebase Storage
 * @param {string[]} imageUrls - Array of image URLs to delete
 * @param {string} userId - The user ID attempting the deletion
 * @param {string} profileId - The profile ID being modified
 * @param {boolean} isAdmin - Whether the user is an admin
 * @returns {Promise<Array>} - Returns array of deletion results
 */
export const deleteGalleryImages = async (imageUrls, userId, profileId, isAdmin = false) => {
  try {
    // Validate ownership once before batch deletion
    if (!userId || !profileId) {
      throw new Error('User ID and Profile ID are required for gallery image deletion');
    }

    const profileData = await getPlayerProfile(profileId);
    if (!profileData) {
      throw new Error('Profile not found');
    }
    
    if (!canEditPlayerProfile(profileData, userId, isAdmin)) {
      throw new Error('Access denied: You do not have permission to delete images from this profile');
    }

    const deletePromises = imageUrls.map(async (url) => {
      try {
        // Call deleteGalleryImage but skip ownership validation since we already checked
        if (!url || !url.includes('firebase')) {
          return { url, success: true };
        }

        // Extract the file path from the URL
        const urlParts = url.split('/');
        const fileToken = urlParts[urlParts.length - 1].split('?')[0];
        const pathParts = decodeURIComponent(fileToken).split('/');
        
        if (pathParts[0] === 'gallery-images') {
          const storageRef = ref(storage, decodeURIComponent(fileToken));
          await deleteObject(storageRef);
        }
        
        return { url, success: true };
      } catch (error) {
        console.error(`Failed to delete ${url}:`, error);
        return { url, success: false, error: error.message };
      }
    });

    return await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error in batch delete:', error);
    throw error;
  }
};

/**
 * Upload a blog image to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} userId - The user ID for the file path
 * @param {string} profileId - The profile ID for the file path
 * @param {string} blogId - The blog ID for the file path
 * @returns {Promise<string>} - Returns the download URL of the uploaded image
 */
export const uploadBlogImage = async (file, userId, profileId, blogId) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload an image smaller than 5MB.');
    }

    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `blog_${timestamp}.${fileExtension}`;

    // Create storage reference
    const storageRef = ref(storage, `blog-images/${userId}/${profileId}/${blogId}/${fileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading blog image:', error);
    throw error;
  }
};

/**
 * Upload an inline blog image to Firebase Storage.
 * Stored separately from hero images at blogs/{userId}/{profileId}/inline/.
 * The returned URL is stored in the blog document's inlineImages map (not in the content string).
 * @param {File} file - The image file to upload
 * @param {string} userId - The user ID for the file path
 * @param {string} profileId - The profile ID for the file path
 * @returns {Promise<string>} - Returns the download URL of the uploaded image
 */
export const uploadBlogInlineImage = async (file, userId, profileId) => {
  try {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload an image smaller than 5MB.');
    }

    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `inline_${timestamp}.${fileExtension}`;

    const storageRef = ref(storage, `blog-images/${userId}/${profileId}/inline/${fileName}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading inline blog image:', error);
    throw error;
  }
};

/**
 * Delete a blog image from Firebase Storage
 * @param {string} imageUrl - The full URL of the image to delete
 * @returns {Promise<void>}
 */
export const deleteBlogImage = async (imageUrl) => {
  try {
    if (!imageUrl || !imageUrl.includes('firebase')) {
      return;
    }

    // Extract the file path from the URL
    const urlParts = imageUrl.split('/');
    const fileToken = urlParts[urlParts.length - 1].split('?')[0];
    const pathParts = decodeURIComponent(fileToken).split('/');

    // Only delete if it's a blog image
    if (pathParts[0] === 'blog-images') {
      const storageRef = ref(storage, decodeURIComponent(fileToken));
      await deleteObject(storageRef);
    }
  } catch (error) {
    console.error('Error deleting blog image:', error);
    throw error;
  }
};

/**
 * Upload a coach hero image to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} coachId - The coach ID for the file path
 * @returns {Promise<string>} - Returns the download URL of the uploaded image
 */
export const uploadCoachHeroImage = async (file, coachId) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload an image smaller than 5MB.');
    }

    // Compress image
    const compressedFile = await compressImage(file, 400, 0.85);

    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `hero_${timestamp}.${fileExtension}`;

    // Create storage reference
    const storageRef = ref(storage, `coach-hero-images/${coachId}/${fileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, compressedFile);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading coach hero image:', error);
    throw error;
  }
};

/**
 * Upload a website block image to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} coachId - The coach's user ID
 * @param {string} blockId - The block ID for organization
 * @returns {Promise<string>} - Returns the download URL of the uploaded image
 */
export const uploadWebsiteBlockImage = async (file, profileId, blockId) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, or WebP image.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size too large. Please upload an image smaller than 5MB.');
    }

    // Compress image (larger width for hero-style banners)
    const compressedFile = await compressImage(file, 1920, 0.85);

    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `block_${blockId}_${timestamp}.${fileExtension}`;

    // Create storage reference
    const storageRef = ref(storage, `profile-blocks/${profileId}/${fileName}`);

    // Upload file
    const snapshot = await uploadBytes(storageRef, compressedFile);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading website block image:', error);
    throw error;
  }
};
